import React, { useState } from 'react';
import { Phone, Mail, Send } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const ScellantContact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          service: "Scellant d'asphalte",
        }),
      });

      if (response.ok) {
        toast({
          title: 'Demande envoyée!',
          description: 'Nous vous contacterons rapidement pour votre soumission gratuite.',
        });
        setFormData({ name: '', address: '', phone: '', email: '' });
      } else {
        throw new Error('Erreur');
      }
    } catch {
      toast({
        title: 'Erreur',
        description: 'Veuillez réessayer ou nous appeler au 819.200.2015',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="scellant-contact" className="py-16 md:py-24 bg-gradient-to-b from-gray-900 to-black">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="text-white">Demandez votre</span>{' '}
            <span className="bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
              soumission gratuite
            </span>
          </h2>
          <p className="text-base md:text-lg text-gray-400">
            Transformez votre entrée d'asphalte. Contactez-nous dès aujourd'hui.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 md:gap-12 max-w-5xl mx-auto">
          <Card className="lg:col-span-2 p-6 md:p-10 border border-red-500/20 bg-gray-900 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="sc-name" className="block text-sm font-semibold text-white mb-1.5">Nom *</label>
                <Input
                  data-testid="scellant-form-name"
                  id="sc-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Votre nom"
                  className="h-12 text-base bg-gray-800 border-red-500/30 text-white"
                />
              </div>

              <div>
                <label htmlFor="sc-address" className="block text-sm font-semibold text-white mb-1.5">Adresse *</label>
                <Input
                  data-testid="scellant-form-address"
                  id="sc-address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Rue Principale, Centre du Québec"
                  className="h-12 text-base bg-gray-800 border-red-500/30 text-white"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="sc-phone" className="block text-sm font-semibold text-white mb-1.5">Téléphone *</label>
                  <Input
                    data-testid="scellant-form-phone"
                    id="sc-phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="819.555.0100"
                    className="h-12 text-base bg-gray-800 border-red-500/30 text-white"
                  />
                </div>
                <div>
                  <label htmlFor="sc-email" className="block text-sm font-semibold text-white mb-1.5">Courriel *</label>
                  <Input
                    data-testid="scellant-form-email"
                    id="sc-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@courriel.com"
                    className="h-12 text-base bg-gray-800 border-red-500/30 text-white"
                  />
                </div>
              </div>

              <Button
                data-testid="scellant-form-submit"
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 text-lg bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white shadow-xl transition-all transform hover:scale-105"
              >
                {isSubmitting ? 'Envoi en cours...' : (
                  <>
                    <Send className="mr-2" size={20} />
                    Envoyer ma demande
                  </>
                )}
              </Button>
            </form>
          </Card>

          <div className="space-y-4">
            <Card className="p-5 border border-red-500/20 bg-gray-900 shadow-lg hover:shadow-xl hover:shadow-red-500/20 transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="bg-gradient-to-br from-red-600 to-red-500 rounded-full p-3 flex-shrink-0">
                  <Phone className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Téléphone</h3>
                  <a href="tel:8192002015" className="text-red-500 hover:text-red-400 font-semibold text-lg">
                    819.200.2015
                  </a>
                </div>
              </div>
            </Card>

            <Card className="p-5 border border-red-500/20 bg-gray-900 shadow-lg hover:shadow-xl hover:shadow-red-500/20 transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="bg-gradient-to-br from-red-600 to-red-500 rounded-full p-3 flex-shrink-0">
                  <Mail className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Courriel</h3>
                  <a href="mailto:groupelavagefenetre@gmail.com" className="text-red-500 hover:text-red-400 font-semibold">
                    groupelavagefenetre@gmail.com
                  </a>
                </div>
              </div>
            </Card>

            <Card className="p-5 border border-red-500/20 bg-gradient-to-br from-gray-900 to-black text-white shadow-lg">
              <h3 className="font-bold text-lg mb-3">Pourquoi nous choisir?</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-0.5">&#10003;</span>
                  <span className="text-gray-300">Pulvérisation professionnelle</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-0.5">&#10003;</span>
                  <span className="text-gray-300">Bitume de qualité supérieure</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-0.5">&#10003;</span>
                  <span className="text-gray-300">Résultat garanti</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScellantContact;
